/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_RATINGS_REVIEWS */
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useProduct } from '@/providers/product-context';
import { getBvLoaderUrl, getBvProductId } from '@/extensions/ratings-reviews/lib/bazaarvoice/bv-url';
import { useBvScript } from '@/extensions/ratings-reviews/lib/bazaarvoice/use-bv-script';

/**
 * Reserve vertical space so the reviews widget hydrating in place doesn't shift
 * layout below it (CLS). Tuned to the empty-state height; BV grows the element
 * as content loads, which only pushes footer content down — never up.
 */
const RESERVED_MIN_HEIGHT = 320;

/** Start loading bv.js when the section is within a viewport of scrolling into view. */
const OBSERVER_ROOT_MARGIN = '600px';

/**
 * Hosted-display Bazaarvoice reviews target for the PDP.
 *
 * Mounts the `<div data-bv-show="reviews" data-bv-product-id="…">` that BV's JS
 * hydrates, and lazily injects the `bv.js` loader — but only once the section
 * scrolls near the viewport (IntersectionObserver). This is the performance
 * guarantee: bv.js never loads on initial render, so it can't affect LCP/TBT.
 *
 * Renders nothing when the extension is disabled or its config is incomplete,
 * so an unconfigured install is a safe no-op.
 */
export default function BvReviewsTarget(): ReactElement | null {
    const product = useProduct();
    const config = useConfig();
    const { site } = useSite();
    const bv = config.extension?.ratingsReviews;

    const containerRef = useRef<HTMLDivElement>(null);
    const bvDivRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);

    const bvProductId = useMemo(() => {
        if (!product?.id) return null;
        return getBvProductId(product.id, {
            productPrefixEnabled: bv?.productPrefixEnabled,
            siteId: site?.id,
        });
    }, [product?.id, bv?.productPrefixEnabled, site?.id]);

    const loaderUrl = useMemo(() => {
        if (!bv?.enabled) return null;
        return getBvLoaderUrl({
            customerName: bv.customerName,
            deploymentZone: bv.deploymentZone,
            environment: bv.environment,
            locale: bv.locale,
            loaderHost: bv.loaderHost,
        });
    }, [bv]);

    // Activate (mount the div + load bv.js) only when the section nears the viewport.
    useEffect(() => {
        if (!loaderUrl || !bvProductId || active) return;
        const el = containerRef.current;
        if (!el) return;
        if (typeof IntersectionObserver === 'undefined') {
            // Environments without IO (very old browsers, tests): activate immediately.
            setActive(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    setActive(true);
                    observer.disconnect();
                }
            },
            { rootMargin: OBSERVER_ROOT_MARGIN }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [loaderUrl, bvProductId, active]);

    const scriptStatus = useBvScript(active ? loaderUrl : null);

    // Re-point the widget to the current product id and ask BV to re-render.
    // Covers variant changes / client-side navigation between products — the
    // React equivalent of SFRA pdpSummary.js's `product:afterAttributeSelect`
    // handler. Guarded on `window.BV.reveal` so it no-ops until bv.js is ready.
    useEffect(() => {
        if (scriptStatus !== 'ready' || !bvProductId) return;
        const el = bvDivRef.current;
        if (el && el.getAttribute('data-bv-product-id') !== bvProductId) {
            el.setAttribute('data-bv-product-id', bvProductId);
        }
        window.BV?.reveal?.();
    }, [scriptStatus, bvProductId]);

    if (!loaderUrl || !bvProductId) return null;

    return (
        <div ref={containerRef} style={{ minHeight: RESERVED_MIN_HEIGHT }}>
            {active && <div ref={bvDivRef} data-bv-show="reviews" data-bv-product-id={bvProductId} />}
        </div>
    );
}
